import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { AsyncSubject, combineLatest, takeUntil } from 'rxjs';
import { SITE_ADMINISTRATOR } from 'src/lib/constants/constants';
import { PermissionEditRolesItypesModel } from 'src/lib/services/api/permission-manager/permission-edit-options.model';
import { PermissionManagerService } from 'src/lib/services/api/permission-manager/permission-manager.service';
import {
	PermissionUserRolesArgument,
	PermissionUserRolesModel,
} from 'src/lib/services/api/permission-manager/permission-user-roles.model';
import { OrganizationsStoreService } from 'src/lib/services/stores/organizations-store/organizations-store.service';
import { PermissionManagerStoreService } from 'src/lib/services/stores/permission-manager-store/permission-manager-store.service';
import { mergeStrings } from 'src/lib/utilities/array';
import { SpinWhileDirective } from '../../../templates/layout/spin-while/spin-while.directive';

@Component({
	selector: 'ae-role-edit-modal',
	templateUrl: './role-edit-modal.component.html',
	styleUrls: ['./role-edit-modal.component.scss'],
	imports: [
		FormsModule,
		NgbTooltipModule,
		NgClass,
		NgSelectModule,
		ReactiveFormsModule,
		SpinWhileDirective,
	],
})
export class RoleEditModalComponent {
	private _unsubscribe$ = new AsyncSubject<null>();
	public item: PermissionUserRolesModel;

	public roleOptions: (PermissionEditRolesItypesModel & {
		disabled?: boolean;
		instituteTooltip?: string;
	})[] = [];
	public selectCtrl: FormControl<string[]>;

	public loading = true;

	protected title = 'Manage User Roles';

	protected instituteRoles: string[] = [];
	protected lockedInstituteRoles: string[] = [];
	private instituteRoleOrganization: Map<string, string> = new Map<
		string,
		string
	>();

	constructor(
		public activeModal: NgbActiveModal,
		private cdref: ChangeDetectorRef,
		private permissionManagerStore: PermissionManagerStoreService,
		private toastr: ToastrService,
		private fb: FormBuilder,
		private permissionManagerService: PermissionManagerService,
		private organizationsStoreService: OrganizationsStoreService,
	) {}

	// Bug Workaround: https://github.com/ng-bootstrap/ng-bootstrap/issues/2645
	public bindModalData = (data: { item: PermissionUserRolesModel }): void => {
		// SET DATA
		this.item = data.item;

		// DETECT CHANGES
		this.cdref.detectChanges();

		// go through roles and disable that option from being deselected for user
		// ex. if gatekeeper is listed, disable the option from being deselected for the user
		this.instituteRoles =
			this.item?.institute_roles?.roles.map((r) => r.role_id) ?? []; // store roles that should be disabled from deselection

		// NOW ALLOWED TO DO BUSINESS LOGIC

		combineLatest([
			this.organizationsStoreService.organizations$(true, true),
			this.permissionManagerStore.editOptions$(),
		])
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe({
				next: ([organizations, o]) => {
					this.item.institute_roles.roles.forEach((r) => {
						const organization = organizations.find(
							(org) => org.id === r.institute_id,
						);
						if (organization) {
							this.instituteRoleOrganization.set(
								r.role_id,
								`${organization.title} ${organization.parent_title ? `(${organization.parent_title})` : ''}`,
							);
						}
					});

					const assignableRoleOptions = Array.from(o.roles).filter(
						(x) =>
							x.has_permission_to_assign === true && x.is_assignable === true,
					);

					this.lockedInstituteRoles = this.item.roles.filter(
						(role) =>
							this.isInstituteRole(role) &&
							assignableRoleOptions.find((x) => x.key === role) != null,
					);

					this.roleOptions = assignableRoleOptions.map((role) => ({
						...role,
						disabled: this.lockedInstituteRoles.includes(role.key),
						instituteTooltip: this.getInstituteRoleTooltip(role.key),
					}));

					this.selectCtrl = this.fb.control<string[]>(
						this.item.roles.filter(
							(x) => this.roleOptions.find((y) => y.key === x) != null,
						),
					);

					this.loading = false;
				},
				error: (err) => {
					this.toastr.error(mergeStrings(err), 'Manage User Roles');
					this.activeModal.close();
				},
			});
	};

	public save = () => {
		const args: Partial<PermissionUserRolesArgument> = {};

		args.add_roles = this.selectCtrl.value
			.map((r) => {
				if (this.item.roles.find((x) => x === r) == null) {
					return r;
				} else {
					return null;
				}
			})
			.filter((x) => x != null);

		args.remove_roles = this.item.roles
			.filter(
				(x) =>
					x !== SITE_ADMINISTRATOR &&
					x !== 'authenticated' &&
					!this.lockedInstituteRoles.includes(x),
			)
			.map((r) => {
				if (this.selectCtrl.value.find((x) => x === r) == null) {
					return r;
				} else {
					return null;
				}
			})
			.filter((x) => x != null);

		this.permissionManagerService.editUserRoles(this.item.uid, args).subscribe({
			next: () => {
				this.toastr.success(`Roles updated for ${this.item.name}`, this.title);
				this.activeModal.close();
			},
			error: (err) => {
				this.toastr.error(
					`There was an issue updating roles; ${mergeStrings(err)}`,
					this.title,
				);
			},
		});
	};

	private isInstituteRole = (role: string) => {
		return this.instituteRoles.includes(role);
	};

	protected getInstituteRoleTooltip = (role: string): string | null => {
		if (this.isInstituteRole(role)) {
			return `Assigned by Program: ${this.instituteRoleOrganization.get(role)}`;
		}
		return null;
	};

	public removeSelectedRole = (role: string, event: Event): void => {
		event.preventDefault();
		event.stopPropagation();

		if (this.lockedInstituteRoles.includes(role) || !this.selectCtrl?.value) {
			return;
		}

		this.selectCtrl.setValue(
			this.selectCtrl.value.filter((selectedRole) => selectedRole !== role),
		);
		this.selectCtrl.markAsDirty();
	};
}
